/**
 * apps/web — public SPA entrypoint. Replaces apps/marketing (Astro).
 *
 * Stack: Vite + React 19 + React Router 6 + @pilot-protocol/ui design
 * system + @pilot-protocol/theme tokens.
 *
 * Env vars (`VITE_*` or `PUBLIC_*` prefix):
 *   - `PUBLIC_API_BASE_URL` — matchmaker / live-match base. Defaults to
 *     `https://api.pilotprotocol.gg` in prod, set to `http://localhost:8080`
 *     in dev to point at a local api-app.
 *   - `PUBLIC_REPLAY_BASE_URL` — replays object-storage base. Defaults
 *     to `https://replays.pilotprotocol.gg`.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, themes } from "@pilot-protocol/theme";
import { configureBaseUrls } from "@pilot-protocol/api-client";
import "@pilot-protocol/ui/styles.css";
import App from "./App";

const apiBaseUrl = (import.meta.env.PUBLIC_API_BASE_URL ??
  import.meta.env.VITE_API_BASE_URL) as string | undefined;
if (apiBaseUrl) {
  configureBaseUrls({ matchmakerBaseUrl: apiBaseUrl });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider themes={themes}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
